import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { ConnectionState } from "../sdk/types/transport";
import {
  TradePriceConfig,
  LastPriceData,
  MarkPriceData,
  isLastPriceData,
  isMarkPriceData,
  RecentTradeDos,
  transformRawTradesToRecentTradeDos,
} from "../sdk/types/data";
import {
  normalizeTimestamp,
  normalizeProtobufTimestamp,
} from "../sdk/utils/time";
import { useMarkets } from "./useMarkets";
import type {
  MultiFeedLastPriceData,
  MultiFeedMarkPriceData,
} from "../sdk/types/api";

// Re-export for backward compatibility
export type { TradePriceConfig, LastPriceData, MarkPriceData };
export { isLastPriceData, isMarkPriceData };

/**
 * Hook for managing last trade price data with HTTP initialization and WebSocket real-time updates
 * Loads the most recent 100 trades from HTTP, then subscribes to WebSocket for new updates
 * Displays the most recent trade price with side indicator (BUY/SELL)
 */
export function useTradePrice(config: TradePriceConfig) {
  // Use useMarkets to derive symbol from marketId
  const { getSymbolByMarketId } = useMarkets({
    baseURL: config.baseURL,
    autoFetchMarkets: true,
  });

  const [lastPrice, setLastPrice] = useState<LastPriceData | null>(null);
  const [markPrice, setMarkPrice] = useState<MarkPriceData | null>(null);
  const [recentTrades, setRecentTrades] = useState<RecentTradeDos[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [priceChange, setPriceChange] = useState<"up" | "down" | null>(null);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const isLastPriceSubscribedRef = useRef<boolean>(false);
  const isMarkPriceSubscribedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<number | null>(null);
  const currentMarketIdRef = useRef<string | null>(null);
  const currentSymbolRef = useRef<string | null>(null);

  /**
   * Poll recent trades from HTTP request (for fallback when WebSocket fails)
   * Doesn't set loading state to avoid UI flicker during polling
   */
  const pollRecentTrades = useCallback(async () => {
    if (!infoClientRef.current) {
      console.warn(
        "[useTradePrice] pollRecentTrades called but infoClientRef.current is null",
      );
      return;
    }

    // Validate marketId is not empty
    if (!config.marketId || config.marketId.trim() === "") {
      console.warn(
        "[useTradePrice] pollRecentTrades called but marketId is empty",
      );
      setError("Market ID is required but was not provided");
      return;
    }
    try {
      const limit = config.limit || 100;
      const trades = await infoClientRef.current.getRecentTrades(
        config.marketId,
        limit,
      );

      if (trades && Array.isArray(trades) && trades.length > 0) {
        // Transform all trades for table display
        const formattedTrades = transformRawTradesToRecentTradeDos(trades);
        setRecentTrades(formattedTrades);

        // Sort by timestamp descending to get the most recent trade
        const sortedTrades = [...trades].sort((a, b) => {
          const timeA = normalizeTimestamp(a.timestamp);
          const timeB = normalizeTimestamp(b.timestamp);
          return timeB - timeA; // Descending: newest first
        });
        const mostRecent = sortedTrades[0];
        // Convert satoshi strings to decimal numbers
        // Use utility function from math.ts for consistent conversion
        const lastPriceData: LastPriceData = {
          market_id: config.marketId || mostRecent.market_id || "",
          price: toNumberFromSatoshi(mostRecent.price),
          quantity: toNumberFromSatoshi(mostRecent.quantity),
          timestamp: normalizeTimestamp(mostRecent.timestamp),
          is_buyer_maker: false, // RawTrade doesn't have is_buyer_maker, default to false
          trade_id: mostRecent.trade_id || "",
        };

        setLastPrice((current) => {
          // Always update to ensure timestamp and other fields are refreshed
          // Determine price change direction
          if (current && typeof current.price === "number") {
            if (lastPriceData.price > current.price) {
              setPriceChange("up");
            } else if (lastPriceData.price < current.price) {
              setPriceChange("down");
            }
          }
          // Clear price change indicator after animation
          setTimeout(() => setPriceChange(null), 500);
          // Always return new object to ensure React detects the change
          return lastPriceData;
        });
      } else {
        console.warn(
          "[useTradePrice] Polling returned no trades or invalid data:",
          trades,
        );
        // Clear trades if no data
        setRecentTrades([]);
      }
    } catch (err) {
      // Only set error if we don't have existing data (to avoid overwriting with transient errors)
      setError(
        err instanceof Error ? err.message : "Failed to load recent trades",
      );
      console.error("[useTradePrice] Failed to poll recent trades:", err);
    }
  }, [config.marketId, config.limit]);

  /**
   * Initialize recent trades from HTTP request
   * Fetches the most recent 100 trades and sets the latest one as lastPrice
   */
  const initializeRecentTrades = useCallback(async () => {
    if (!infoClientRef.current) return;

    // Validate marketId is not empty
    if (!config.marketId || config.marketId.trim() === "") {
      console.warn(
        "[useTradePrice] initializeRecentTrades called but marketId is empty",
      );
      setLoading(false);
      setError("Market ID is required but was not provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const limit = config.limit || 100;
      const trades = await infoClientRef.current.getRecentTrades(
        config.marketId,
        limit,
      );

      if (trades && Array.isArray(trades) && trades.length > 0) {
        // Transform all trades for table display
        const formattedTrades = transformRawTradesToRecentTradeDos(trades);
        setRecentTrades(formattedTrades);

        // Sort by timestamp descending to get the most recent trade
        const sortedTrades = [...trades].sort((a, b) => {
          const timeA = normalizeTimestamp(a.timestamp);
          const timeB = normalizeTimestamp(b.timestamp);
          return timeB - timeA; // Descending: newest first
        });

        const mostRecent = sortedTrades[0];
        /**
         [useTradePrice] Most recent trade:
        {
        trade_id: "trade_1765866649599060794",
        buy_order_id: "7bba9d0495eea08c9239ad050bfaf27fbcaabb2312851ca3dc66716bc89dc6eb",
        sell_order_id: "65e6c9ff85fce05cce7df08c4bc14187aa93476e8595e865dde9611e560d239d",
        price: "8198750305175",
        quantity: "1157786",
        timestamp: "1765866649",
        market_id: "0",
        maker_fee: "0",
        taker_fee: "0",
        dual_write_id: "16210999351379558260",
        }
         */
        // Convert satoshi strings to decimal numbers
        // Use utility function from math.ts for consistent conversion
        const lastPriceData: LastPriceData = {
          market_id: config.marketId || mostRecent.market_id || "",
          price: toNumberFromSatoshi(mostRecent.price),
          quantity: toNumberFromSatoshi(mostRecent.quantity),
          timestamp: normalizeTimestamp(mostRecent.timestamp),
          is_buyer_maker: false, // RawTrade doesn't have is_buyer_maker, default to false
          trade_id: mostRecent.trade_id || "",
        };
        setLastPrice(lastPriceData);
      } else {
        console.warn(
          "[useTradePrice] Initialization returned no trades or invalid data:",
          trades,
        );
        // Clear trades if no data
        setRecentTrades([]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load recent trades",
      );
      console.error("Failed to initialize recent trades:", err);
      // Clear trades on error
      setRecentTrades([]);
    } finally {
      setLoading(false);
    }
  }, [config.marketId, config.limit]);

  /**
   * Handle last price update from WebSocket
   */
  const handleLastPriceUpdate = useCallback(
    (data: MultiFeedLastPriceData) => {
      console.debug(
        "[useTradePrice] handleLastPriceUpdate called with data:",
        data,
      );

      if (!data) {
        console.warn(
          "[useTradePrice] handleLastPriceUpdate received null/undefined data",
        );
        return;
      }

      if (!data.price || data.price === "") {
        console.warn(
          "[useTradePrice] handleLastPriceUpdate received data without price field:",
          data,
        );
        return;
      }

      // Skip derived prices (not actual trades) - still update lastPrice for display
      if (data.derived === true) {
        console.debug(
          "[useTradePrice] Received derived price update (not an actual trade), will update lastPrice but not recentTrades",
        );
      }

      // Ensure price is a number (SubscriptionClient should convert, but be defensive)
      // Use utility function from math.ts for consistent conversion
      const price = toNumberFromSatoshi(data.price);
      const quantity = toNumberFromSatoshi(data.quantity);

      if (isNaN(price)) {
        console.warn("[useTradePrice] Invalid price value:", data.price);
        return;
      }

      const timestamp = normalizeProtobufTimestamp(data.timestamp);

      setLastPrice((current) => {
        // Determine price change direction
        if (isLastPriceData(current) && typeof current.price === "number") {
          if (price > current.price) {
            setPriceChange("up");
          } else if (price < current.price) {
            setPriceChange("down");
          }
        }

        // Clear price change indicator after animation
        setTimeout(() => setPriceChange(null), 500);

        const lastPriceData: LastPriceData = {
          market_id: config.marketId || data.market_id || "",
          price: price,
          quantity: quantity,
          timestamp: timestamp,
          is_buyer_maker: data.is_buyer_maker || false,
          trade_id: data.trade_id || "",
        };

        // Type validation for debugging
        if (!isLastPriceData(lastPriceData)) {
          console.error(
            "[useTradePrice] Invalid LastPriceData structure:",
            lastPriceData,
          );
        }

        console.debug("[useTradePrice] Setting lastPrice to:", lastPriceData);
        console.debug(
          "[useTradePrice] Type check - isLastPriceData:",
          isLastPriceData(lastPriceData),
        );
        return lastPriceData;
      });

      // Add new trade to recentTrades array (only if not derived - actual trade)
      if (!data.derived) {
        setRecentTrades((current) => {
          // Convert MultiFeedLastPriceData to RecentTradeDos format
          const newTrade: RecentTradeDos = {
            time: timestamp,
            size: quantity,
            price: price,
            is_buyer_maker: data.is_buyer_maker || false,
          };

          // Check if this trade already exists (by timestamp+price+quantity to avoid duplicates)
          const tradeExists = current.some(
            (trade) =>
              trade.time === timestamp &&
              trade.price === price &&
              trade.size === quantity,
          );

          if (tradeExists) {
            console.debug(
              "[useTradePrice] Trade already exists in recentTrades, skipping duplicate",
            );
            return current;
          }

          // Add new trade to the beginning (newest first)
          const updatedTrades = [newTrade, ...current];

          // Limit to configured limit (default 100) to prevent array from growing too large
          const limit = config.limit || 100;
          const limitedTrades = updatedTrades.slice(0, limit);

          console.debug(
            "[useTradePrice] Added new trade to recentTrades:",
            newTrade,
          );
          return limitedTrades;
        });
      }
    },
    [config.marketId, config.limit, getSymbolByMarketId],
  );

  /**
   * Handle mark price update from WebSocket
   */
  const handleMarkPriceUpdate = useCallback(
    (data: MultiFeedMarkPriceData) => {
      if (!data) {
        console.warn(
          "[useTradePrice] handleMarkPriceUpdate received null/undefined data",
        );
        return;
      }

      if (data.mark_price === undefined || data.mark_price === null) {
        console.warn(
          "[useTradePrice] handleMarkPriceUpdate received data without mark_price field:",
          data,
        );
        return;
      }

      // Convert mark_price from satoshi string to decimal number
      // Use utility function from math.ts for consistent conversion
      const markPrice = toNumberFromSatoshi(data.mark_price);

      if (isNaN(markPrice)) {
        console.warn(
          "[useTradePrice] Invalid mark_price value:",
          data.mark_price,
        );
        return;
      }

      // Derive symbol from marketId using useMarkets
      const symbol = getSymbolByMarketId(config.marketId) || "";
      const markPriceData: MarkPriceData = {
        symbol: data.symbol || symbol,
        mark_price: markPrice, // Converted to decimal number
        timestamp: normalizeProtobufTimestamp(data.timestamp),
      };

      // Type validation for debugging
      if (!isMarkPriceData(markPriceData)) {
        console.error(
          "[useTradePrice] Invalid MarkPriceData structure:",
          markPriceData,
        );
      }

      setMarkPrice(markPriceData);
    },
    [config.marketId, getSymbolByMarketId],
  );

  // Initialize clients
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    infoClientRef.current = infoClient;

    const subscriptionClient = config.subscriptionClient;

    // Initialize recent trades from HTTP
    initializeRecentTrades();

    subscriptionClientRef.current = subscriptionClient;

    // Subscribe function that can be called on initial connect or reconnection
    const subscribe = () => {
      // Validate marketId is not empty before subscribing
      if (!config.marketId || config.marketId.trim() === "") {
        console.warn("[useTradePrice] Cannot subscribe: marketId is empty");
        setError("Market ID is required but was not provided");
        return;
      }

      // Check if ready for subscriptions (connected and authenticated if required)
      if (!subscriptionClient.isReadyForSubscriptions()) {
        if (
          subscriptionClient.isConnected() &&
          !subscriptionClient.isReadyForSubscriptions()
        ) {
          // Connected but not authenticated - wait a bit and retry
          const authState = subscriptionClient.getAuthState();
          if (authState === "pending") {
            console.debug(
              "[useTradePrice] Waiting for authentication to complete...",
            );
            // Will retry on next state change
          } else if (authState === "failed") {
            console.error(
              "[useTradePrice] Authentication failed - cannot subscribe to trade price",
            );
            setError("Authentication failed - cannot subscribe to trade price");
          }
        }
        return;
      }

      // Check if we need to re-subscribe (marketId or symbol changed, or not yet subscribed)
      const needsResubscribe =
        !isLastPriceSubscribedRef.current || !isMarkPriceSubscribedRef.current;

      if (needsResubscribe) {
        // Unsubscribe from previous subscriptions if they exist
        if (isLastPriceSubscribedRef.current && subscriptionClientRef.current) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "last_price",
              handleLastPriceUpdate,
            );
          } catch (error) {
            console.warn(
              "[useTradePrice] Error unsubscribing from previous last_price subscription:",
              error,
            );
          }
        }
        if (isMarkPriceSubscribedRef.current && subscriptionClientRef.current) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "mark_price",
              handleMarkPriceUpdate,
            );
          } catch (error) {
            console.warn(
              "[useTradePrice] Error unsubscribing from previous mark_price subscription:",
              error,
            );
          }
        }

        try {
          // Subscribe to mark price if marketId is provided
          if (config.marketId) {
            // Derive symbol from marketId using useMarkets
            subscriptionClient.subscribeMultiFeedSubChannel(
              "last_price",
              config.marketId,
              handleLastPriceUpdate,
            );
            isLastPriceSubscribedRef.current = true;

            subscriptionClient.subscribeMultiFeedSubChannel(
              "mark_price",
              config.marketId,
              handleMarkPriceUpdate,
            );
            isMarkPriceSubscribedRef.current = true;
            console.debug(
              "[useTradePrice] Subscribed to mark_price:",
              config.marketId,
            );
          }
          setLoading(false);
        } catch (error) {
          console.error("[useTradePrice] Failed to subscribe:", error);
          setError(
            error instanceof Error
              ? error.message
              : "Failed to subscribe to trade price data",
          );
          isLastPriceSubscribedRef.current = false;
          isMarkPriceSubscribedRef.current = false;
          setLoading(false);
        }
      }
    };

    // Set initial connection state
    setIsConnected(subscriptionClient.isConnected());
    setLoading(false);

    // Try to subscribe immediately if client is ready
    if (subscriptionClient.isReadyForSubscriptions()) {
      subscribe();
    }

    // Monitor connection state changes and re-subscribe on reconnection
    // Use the public onStateChange method from SubscriptionClient
    const unsubscribeStateChange = subscriptionClient.onStateChange(
      (state: ConnectionState) => {
        console.debug("[useTradePrice] WebSocket state changed to:", state);
        setIsConnected(state === ConnectionState.Connected);
        if (state === ConnectionState.Connected) {
          console.debug(
            "[useTradePrice] WebSocket connected - stopping polling if active",
          );
          // Always try to subscribe on connection (subscribe function handles re-subscription logic)
          subscribe();
        } else if (
          state === ConnectionState.Disconnected ||
          state === ConnectionState.Reconnecting
        ) {
          console.debug(
            "[useTradePrice] WebSocket disconnected/reconnecting - polling effect should start polling",
          );
          // Don't reset isLastPriceSubscribedRef here - let subscribe() handle re-subscription
          // This allows us to track if we need to re-subscribe with new params
        }
      },
    );

    return () => {
      isLastPriceSubscribedRef.current = false;
      isMarkPriceSubscribedRef.current = false;

      // Unsubscribe from state changes
      if (unsubscribeStateChange) {
        unsubscribeStateChange();
      }

      if (subscriptionClientRef.current) {
        try {
          subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
            "last_price",
            handleLastPriceUpdate,
          );
        } catch (error) {
          console.warn(
            "[useTradePrice] Error unsubscribing during cleanup:",
            error,
          );
        }
        if (config.marketId) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "mark_price",
              handleMarkPriceUpdate,
            );
          } catch (error) {
            console.warn(
              "[useTradePrice] Error unsubscribing mark_price during cleanup:",
              error,
            );
          }
        }
      }
    };
    // Note: We don't include callbacks in dependencies because:
    // 1. They're recreated when their dependencies change (config.marketId)
    // 2. They capture the latest config values via closure
    // 3. We only need to re-run this effect when actual config values change, not when callbacks are recreated
    // Symbol is derived from marketId via useMarkets, so we only need marketId in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.subscriptionClient,
    config.baseURL,
    config.marketId,
    handleLastPriceUpdate,
    handleMarkPriceUpdate,
  ]);

  // Re-initialize when marketId or symbol changes
  useEffect(() => {
    const subscriptionClient = config.subscriptionClient;
    if (
      infoClientRef.current &&
      subscriptionClient &&
      subscriptionClientRef.current
    ) {
      // Validate marketId is not empty
      if (!config.marketId || config.marketId.trim() === "") {
        console.warn("[useTradePrice] Cannot re-initialize: marketId is empty");
        setError("Market ID is required but was not provided");
        return;
      }

      // Derive symbol from marketId using useMarkets
      const symbol = getSymbolByMarketId(config.marketId) || "BTC/USDC"; // Default symbol if not found
      const previousMarketId = currentMarketIdRef.current;
      const previousSymbol = currentSymbolRef.current;

      // Unsubscribe from previous market/symbol if they changed and client is connected
      if (
        subscriptionClient.isConnected() &&
        previousMarketId !== null &&
        (previousMarketId !== config.marketId || previousSymbol !== symbol)
      ) {
        try {
          // Always unsubscribe callbacks when marketId or symbol changes
          subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
            "last_price",
            handleLastPriceUpdate,
          );
          subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
            "mark_price",
            handleMarkPriceUpdate,
          );
          isLastPriceSubscribedRef.current = false;
          isMarkPriceSubscribedRef.current = false;
        } catch (error) {
          console.warn(
            "[useTradePrice] Error unsubscribing from previous market:",
            error,
          );
        }
      }

      // Initialize new market
      initializeRecentTrades();

      // Subscribe to new market if client is ready
      if (subscriptionClient.isReadyForSubscriptions()) {
        try {
          // Subscribe to last_price for the new marketId/symbol
          subscriptionClientRef.current.subscribeMultiFeedSubChannel(
            "last_price",
            config.marketId,
            handleLastPriceUpdate,
          );
          isLastPriceSubscribedRef.current = true;
          console.debug("[useTradePrice] Subscribed to last_price:", {
            marketId: config.marketId,
          });

          // Subscribe to mark_price for the new marketId/symbol
          subscriptionClientRef.current.subscribeMultiFeedSubChannel(
            "mark_price",
            config.marketId,
            handleMarkPriceUpdate,
          );
          isMarkPriceSubscribedRef.current = true;
          console.debug("[useTradePrice] Subscribed to mark_price:", {
            marketId: config.marketId,
          });
        } catch (error) {
          console.error("[useTradePrice] Failed to re-subscribe:", error);
          setError(
            error instanceof Error
              ? error.message
              : "Failed to subscribe to trade price data",
          );
          isLastPriceSubscribedRef.current = false;
          isMarkPriceSubscribedRef.current = false;
        }
      }

      // Update refs to track current marketId and symbol
      currentMarketIdRef.current = config.marketId;
      currentSymbolRef.current = symbol;
    }
    // Note: We don't include callbacks in dependencies because:
    // 1. They're recreated when config.marketId changes
    // 2. They capture the latest config values via closure
    // 3. We only need to re-run when marketId changes, not when callbacks are recreated
    // Symbol is derived from marketId via useMarkets, so we only need marketId in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.subscriptionClient, config.marketId]);

  // Fallback to HTTP polling when WebSocket is disconnected (unless disabled)
  useEffect(() => {
    console.debug(
      "[useTradePrice] Polling effect triggered - isConnected:",
      isConnected,
      "marketId:",
      config.marketId,
      "disablePolling:",
      config.disablePolling,
    );

    // Clear any existing polling interval
    if (pollingIntervalRef.current !== null) {
      console.debug(
        "[useTradePrice] Clearing existing polling interval (polling effect)",
      );
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Start polling if WebSocket is not connected and polling is explicitly enabled (disablePolling === false)
    // By default, polling is disabled (when disablePolling is undefined, treat as true/disabled)
    if (
      !isConnected &&
      infoClientRef.current &&
      config.disablePolling === false
    ) {
      console.debug(
        "[useTradePrice] Starting HTTP polling (WebSocket disconnected, marketId:",
        config.marketId,
        ")",
      );
      // Start polling immediately, then every 5 seconds
      pollRecentTrades();
      pollingIntervalRef.current = window.setInterval(() => {
        pollRecentTrades();
      }, 5000);
      console.debug(
        "[useTradePrice] HTTP polling interval set up (every 5 seconds)",
      );
    } else if (!isConnected && !infoClientRef.current) {
      console.warn(
        "[useTradePrice] Cannot start polling: infoClientRef.current is null (isConnected:",
        isConnected,
        ")",
      );
    } else if (isConnected) {
      console.debug("[useTradePrice] WebSocket connected, polling not needed");
    } else if (config.disablePolling !== false) {
      // Polling is disabled by default (when undefined) or explicitly disabled
      console.debug(
        "[useTradePrice] Polling disabled by config (default behavior)",
      );
    }

    return () => {
      if (pollingIntervalRef.current !== null) {
        console.debug(
          "[useTradePrice] Cleaning up polling interval (polling effect cleanup)",
        );
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // Note: pollRecentTrades is a useCallback that depends on config.marketId and config.limit
    // We don't need to include it in dependencies - it will use the latest values via closure
    // We only need to re-run when isConnected, marketId, or disablePolling changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, config.marketId, config.disablePolling]);

  // Compute display price and timestamp: use whichever has the later timestamp (lastPrice or markPrice)
  // Note: markPrice subscription ensures markPrice is always available when symbol is provided
  const { displayPrice, displayTimestamp } = useMemo(() => {
    const hasLastPrice = isLastPriceData(lastPrice) && lastPrice.price != null;
    const hasMarkPrice =
      isMarkPriceData(markPrice) && markPrice.mark_price != null;

    // If both are available, compare timestamps and use the more recent one
    if (hasLastPrice && hasMarkPrice) {
      const lastPriceTimestamp = lastPrice.timestamp || 0;
      const markPriceTimestamp = markPrice.timestamp || 0;

      if (lastPriceTimestamp >= markPriceTimestamp) {
        // lastPrice is more recent or equal

        return {
          displayPrice: lastPrice.price,
          displayTimestamp: lastPriceTimestamp,
        };
      } else {
        return {
          displayPrice: markPrice.mark_price, // Already a number, no parsing needed
          displayTimestamp: markPriceTimestamp,
        };
      }
    }

    // If only lastPrice is available (markPrice not yet received or symbol not provided)
    if (hasLastPrice) {
      return {
        displayPrice: lastPrice.price,
        displayTimestamp: lastPrice.timestamp || 0,
      };
    }

    // If only markPrice is available (lastPrice not yet received)
    // markPrice subscription ensures this is always available when symbol is provided
    if (hasMarkPrice) {
      return {
        displayPrice: markPrice.mark_price, // Already a number, no parsing needed
        displayTimestamp: markPrice.timestamp || 0,
      };
    }

    // This should only occur during initial loading before markPrice subscription establishes
    // Once markPrice subscription is active (when symbol is provided), markPrice will always be available
    // If symbol is not provided, lastPrice should be available from last_price subscription
    return {
      displayPrice: null,
      displayTimestamp: null,
    };
  }, [lastPrice, markPrice]); // Use whole objects to ensure updates are detected

  return {
    lastPrice,
    markPrice,
    displayPrice,
    displayTimestamp,
    recentTrades, // Expose all trades for table display
    loading,
    error,
    isConnected,
    priceChange,
    refresh: initializeRecentTrades,
    // Export InfoClient for reuse in components (deprecated - use recentTrades from hook instead)
    infoClient: infoClientRef.current,
    // Export type guards for external debugging
    isLastPriceData,
    isMarkPriceData,
  };
}
